<template>
    <b-modal
        v-model="showModal"
        size="lg"
        centered
        hide-footer
        :title="$t('ExportCertificate')"
        body-class="p-4"
        no-close-on-backdrop>
        <div class="w-100">
            <div class="d-flex justify-content-end mb-2">
                <b-button 
                    size="sm" 
                    :variant="'outline-' + Variant" 
                    @click="toggleSelectAll">
                    {{ isAllSelected ? $t('UnselectAll') : $t('SelectAll') }}
                </b-button>
            </div>
            <b-table
                @row-clicked="onRowClicked"
                :per-page="perPage"
                :current-page="currentPage"
                :items="filteredCertificates"
                :fields="fields"
                :tbody-tr-class="rowClass"
                primary-key="CertificateId"
                responsive
                hover
                bordered
                show-empty
                :empty-text="$t('NoCertificateToShow')"
                style="cursor: pointer;">
                <template #cell(selected)="data">
                    <b-form-checkbox 
                        :checked="isSelected(data.item)"
                        :variant="Variant"
                        style="pointer-events: none;">
                    </b-form-checkbox>
                </template>
                <template #cell(Distributor)="data">
                    <b-img
                        v-if="data.item && data.item.Distributor !== undefined"
                        :src="getLogo(sMACertificateEnum[data.item.Distributor])"
                        :width="50" 
                        alt="Logo"
                        v-b-tooltip.html
                        :title="sMACertificateEnum[data.item.Distributor]"/>
                </template>
                <template #cell(CertificateName)="data">
                    {{ data.item.CertificateName }}
                </template>
            </b-table>
            <b-pagination
                v-if="filteredCertificates.length > perPage"
                v-model="currentPage"
                :total-rows="filteredCertificates.length"
                :per-page="perPage"
                align="center"
                class="mt-3"
                size="sm">
            </b-pagination>
            <div class="mt-4 w-100 d-flex justify-content-end">
                <b-button 
                    :variant="'outline-' + Variant" 
                    class="mr-2"
                    pill
                    @click="closeModal">
                    {{ $t('Cancel') }}
                </b-button>
                <b-button 
                    :variant="Variant" 
                    @click="onSubmit" 
                    pill
                    :disabled="selectedCertificates.length === 0">
                    {{ $t('Export') }}
                </b-button>
            </div>
        </div>
    </b-modal>
</template>

<script lang="ts">
/**
 * Composant Vue js
 */
import { Component, Prop, PropSync, Vue } from 'vue-property-decorator';

/**
 * Classe mandat & Enumérations
 */
import { SMACertificate } from '@/types/Administration/SMA/SMACertificate';
import SMACertificateEnum from '@/enums/Administration/SMA/SMACertificateEnum';

/**
 * Traductions
 */
import locale from '../locales/SMA.json';

@Component({
    i18n: { messages: locale }
})
/**
 * Modale de sélection de mandats
 */
export default class CertificateSelector extends Vue {

    /**
     * Indique si la modale est visible (Lié à la prop "Show" du parent)
     */
    @PropSync('Show', { type: Boolean, required: true })
    private showModal!: boolean;

    /**
     * Liste complète des mandats fournie par le parent
     */
    @Prop({ type: Array, required: true, default: () => [] }) 
    private Certificates!: SMACertificate[];

    /**
     * Variant de couleur à appliquer sur le composant 
     */
    @Prop() 
    public Variant!: string;

    /**
     * Liste des mandats actuellement sélectionnés
     */
    private selectedCertificates: SMACertificate[] = [];

    /**
     * Énumération des distributeurs
     */
    private sMACertificateEnum = SMACertificateEnum;

    /**
     * Données de pagination
     */
    private currentPage: number = 1;
    private perPage: number = 5; 

    /**
     * Configuration des colonnes du tableau BootstrapVue
     */
    private fields: { key: string, label: string, sortable?: boolean, thClass?: string, tdClass?: string }[] = [
        { key: 'selected', label: '', thClass: 'text-center', tdClass: 'text-center align-middle' },
        { key: 'Distributor', label: 'Distributeur', sortable: true, tdClass: 'align-middle' },
        { key: 'CertificateName', label: 'Nom du certificat', sortable: true, tdClass: 'align-middle' }
    ];

    /**
     * Filtre la liste pour ne conserver que les mandats ayant le Statut 3
     */
    private get filteredCertificates(): SMACertificate[] {
        return this.Certificates.filter(cert => cert.Status === 3);
    }

    /**
     * Vérifie si un mandat est présent dans la liste des éléments sélectionnés
     * @param item Le mandat à vérifier
     */
    private isSelected(item: SMACertificate): boolean {
        return this.selectedCertificates.some(cert => cert.CertificateId === item.CertificateId);
    }
    
    /**
     * Vérifie si tous les certificats filtrés sont actuellement sélectionnés
     */
    private get isAllSelected(): boolean {
        return this.filteredCertificates.length > 0 && 
               this.selectedCertificates.length === this.filteredCertificates.length;
    }

    /**
     * Alterne entre "Tout sélectionner" et "Tout désélectionner"
     */
    private toggleSelectAll(): void {
        if (this.isAllSelected) {
            // Si tout est déjà sélectionné, on vide le tableau
            this.selectedCertificates = [];
        } else {
            // Sinon, on copie l'intégralité des éléments filtrés dans la sélection
            this.selectedCertificates = [...this.filteredCertificates];
        }
    }

    /**
     * Méthode appelée lors d'un clic sur n'importe quel endroit de la ligne
     * Ajoute ou retire le mandat de la sélection
     * @param item Le mandat cliqué
     */
    private onRowClicked(item: SMACertificate): void {
        const index = this.selectedCertificates.findIndex(cert => cert.CertificateId === item.CertificateId);
        
        if (index > -1) {
            // S'il est déjà sélectionné, on le retire
            this.selectedCertificates.splice(index, 1);
        } else {
            // Sinon, on l'ajoute
            this.selectedCertificates.push(item);
        }
    }

    /**
     * Ajoute dynamiquement la classe Bootstrap 'table-primary' aux lignes sélectionnées
     * pour un rendu visuel immédiat.
     */
    private rowClass(item: SMACertificate, type: string): string {
        if (!item || type !== 'row') return '';
        return this.isSelected(item) ? `table-${this.Variant}` : '';
    }

    /**
     * Retourne l'url du logo du distributeur : en svg si disponible, sinon en webp
     * @param name Nom du distributeur
     */
    private getLogo(name: string) : string
    {
        try
        {
            return require(`@/assets/images/${name}.svg`);
        }
        catch
        {
            return require(`@/assets/images/${name}.webp`);
        }
    }
    
    /**
     * Ferme la modale, réinitialise la pagination et vide la sélection
     */
    private closeModal(): void {
        this.showModal = false;
        this.currentPage = 1; 
        this.selectedCertificates = [];
    }

    /**
     * Lors de la validation de la sélection.
     * Génère l'événement $emit('validation', this.selectedCertificates) et ferme la modale.
     */
    private onSubmit(): void {
        const result = [...this.selectedCertificates];
        this.selectedCertificates = []; 
        
        this.$emit("Submit", result)
    }
}
</script>
<style scoped>
    /* Met à jour la couleur de fond du switch du sélecteur de work lorsqu'il est coché */
    :deep(.custom-control-input:checked ~ .custom-control-label::before) {
            border-color: var(--Variant) !important;
            background-color: var(--Variant) !important;
        }

    /* Met à jour la couleur de fond du switch du sélecteur de work lorsqu'il est actif */
    :deep(.custom-control-input:active ~ .custom-control-label::before) {
        background-color: var(--Variant) !important;
        border-color: var(--Variant) !important;
    }
</style>